import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuditLogResponse } from '../../../audit/models/audit.model';
import { AuditDisplayService, AuditDiffRow } from '../../../../shared/services/audit-display.service';
import { DatetimeService } from '../../../../shared/services/datetime.service';
import {
  FormTimelineEntry,
  FormTimelineVisualType,
  enrichFormTimeline,
  formEntryBadgeLabel,
  formEntryBadgeClass,
  formEntryDotStyle,
  formEntryIconPath,
  DotStyle,
} from '../../utils/mkt-form-audit-timeline.util';

@Component({
  selector: 'app-form-audit-timeline',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './form-audit-timeline.component.html',
})
export class FormAuditTimelineComponent {

  @Input() set logs(value: AuditLogResponse[]) {
    this.entries = enrichFormTimeline(value ?? []);
  }
  @Input() loading = false;
  @Input() error = '';
  @Input() currentPage = 0;
  @Input() totalPages = 0;
  @Input() totalElements = 0;
  @Input() emptyMessage = 'No hay eventos de auditoría para este formulario.';

  @Output() pageChange = new EventEmitter<number>();

  entries: FormTimelineEntry[] = [];
  expandedId: number | null = null;

  constructor(
    private auditDisplay: AuditDisplayService,
    public datetime: DatetimeService,
  ) {}

  toggle(id: number): void {
    this.expandedId = this.expandedId === id ? null : id;
  }

  // ── Visual helpers ────────────────────────────────────────────────────────

  badgeLabel(vt: FormTimelineVisualType): string  { return formEntryBadgeLabel(vt); }
  badgeClass(vt: FormTimelineVisualType): string  { return formEntryBadgeClass(vt); }
  dotStyle(vt: FormTimelineVisualType): DotStyle  { return formEntryDotStyle(vt); }
  iconPath(vt: FormTimelineVisualType): string    { return formEntryIconPath(vt); }

  // Status-specific events are self-contained — entity chip adds no info
  private readonly STANDALONE_TYPES = new Set<FormTimelineVisualType>(
    ['PUBLISH', 'PAUSE', 'ARCHIVE', 'UNARCHIVE', 'DELETE']
  );

  showEntityChip(entry: FormTimelineEntry): boolean {
    return !this.STANDALONE_TYPES.has(entry.visualType);
  }

  entityLabel(entry: FormTimelineEntry): string {
    return this.auditDisplay.translateEntity(entry.entityName);
  }

  // ── Content helpers ───────────────────────────────────────────────────────

  headline(entry: FormTimelineEntry): string {
    return this.auditDisplay.getTimelineHeadline(entry);
  }

  hasDiff(entry: FormTimelineEntry): boolean {
    return this.auditDisplay.hasDiff(entry);
  }

  diffRows(entry: FormTimelineEntry): AuditDiffRow[] {
    return this.auditDisplay.getDiffRows(entry);
  }

  changeSummary(entry: FormTimelineEntry): string {
    return this.auditDisplay.getChangeSummary(entry);
  }

  changedFieldLabels(entry: FormTimelineEntry): string[] {
    return this.auditDisplay.getChangedFieldLabels(entry);
  }
}
